// TODUE V2 — atomické číslovanie faktúr (AC6).
// Counter žije v Dexie `settings` store (key 'invoice_counter', default 0).
// Formát čísla: {YYYY}{NNN} — rok + 3-miestne poradové číslo (re-use semantiky
// V1 formatInvoiceNumber). Increment je ATOMICKÝ — beží v Dexie 'rw' transakcii
// nad settings store, takže dve súbežné vystavenia nedostanú rovnaké číslo.
//
// 0 fetch / 0 Supabase / 0 auth / 0 LLM — čistá lokálna Dexie operácia.
import { db, putSetting } from './db';

export const INVOICE_COUNTER_KEY = 'invoice_counter';

// Doklady s vlastným číselným radom (W2 + taxonómia). 'invoice' = klasická
// faktúra (default, spätne kompatibilná s pôvodným jediným radom). Ostatné typy
// majú vlastný counter + prefix. Kanonické hodnoty zrkadlia pack
// sk-doctypes@1.0.0 (parita dáta↔kód strážená drift-guard testom).
export const DOC_KINDS = [
  'invoice',
  'proforma',
  'credit_note',
  'quote',
  'order',
  'delivery_note',
  'receipt',
];

// W2 — samostatný číselný rad per doc kind.
// SPÄTNÁ KOMPATIBILITA: 'invoice' MUSÍ ostať na pôvodnom INVOICE_COUNTER_KEY
// ('invoice_counter') — existujúce faktúry aj counter sa nesmú dotknúť.
// Ostatné typy dostanú vlastný settings kľúč → rady inkrementujú nezávisle.
// Neznámy/chýbajúci kind → fallback na 'invoice' rad (defenzívne, records.js
// aj tak validuje docKind cez DOC_KINDS).
const COUNTER_KEY_BY_KIND = {
  invoice: INVOICE_COUNTER_KEY,
  proforma: 'proforma_counter',
  credit_note: 'credit_note_counter',
  quote: 'quote_counter',
  order: 'order_counter',
  delivery_note: 'delivery_note_counter',
  receipt: 'receipt_counter',
};

export function invoiceCounterKey(kind) {
  return COUNTER_KEY_BY_KIND[kind] || INVOICE_COUNTER_KEY;
}

// W2 — formát čísla per doc kind.
// 'invoice' = presne formatInvoiceNumberV2 ({YYYY}{NNN}) — byte-identické,
// daňová faktúra sa nemení. proforma/credit_note dostanú ľudsky čitateľný
// prefix, aby doklad na prvý pohľad NEvyzeral ako daňová faktúra:
//   proforma      → PF{YYYY}{NNN}  (zálohová/proforma)
//   credit_note   → DP{YYYY}{NNN}  (dobropis)
//   quote         → CP{YYYY}{NNN}  (cenová ponuka)
//   order         → OBJ{YYYY}{NNN} (objednávka)
//   delivery_note → DL{YYYY}{NNN}  (dodací list)
//   receipt       → PD{YYYY}{NNN}  (pokladničný doklad)
// 3-miestny zero-pad + žiadne orezanie pri seq >= 1000 (zrkadlí
// formatInvoiceNumberV2). Neznámy kind → invoice formát (fallback).
// Prefixy zrkadlia pack sk-doctypes@1.0.0 (drift-guard test stráži paritu).
const NUMBER_PREFIX_BY_KIND = {
  invoice: '',
  proforma: 'PF',
  credit_note: 'DP',
  quote: 'CP',
  order: 'OBJ',
  delivery_note: 'DL',
  receipt: 'PD',
};

export function formatNumberForKind(kind, year, seq) {
  const prefix = NUMBER_PREFIX_BY_KIND[kind] || '';
  return `${prefix}${formatInvoiceNumberV2(year, seq)}`;
}

// {YYYY}{NNN} — napr. rok 2026, seq 1 → '2026001'. seq >= 1000 → bez orezania
// (rad nesmie kolabovať), jednoducho viac číslic: 2026 + '1234'.
export function formatInvoiceNumberV2(year, seq) {
  const nnn = String(seq).padStart(3, '0');
  return `${year}${nnn}`;
}

// Číslice z čísla faktúry pre variabilný symbol (default VS = number bez nečíslic).
export function vsFromNumber(number) {
  return String(number || '').replace(/\D/g, '');
}

// Atomicky inkrementuje counter a vráti pridelené číslo faktúry.
// Beží VO VNÚTRI volajúcej transakcie ak je `tx` daný (createInvoice ho volá
// vnútri jednej 'rw' transakcie nad records+settings). Bez `tx` si otvorí vlastnú.
//
// year — voliteľný (default aktuálny rok); umožní deterministický test.
export async function nextInvoiceNumberV2(year = new Date().getFullYear()) {
  const run = async (tx) => {
    const row = await tx.settings.get(INVOICE_COUNTER_KEY);
    const current = Number(row?.value) || 0;
    const next = current + 1;
    await putSetting({ key: INVOICE_COUNTER_KEY, value: next }, tx.settings);
    return { number: formatInvoiceNumberV2(year, next), seq: next };
  };
  // Vlastná atomická top-level transakcia (settings rw) cez engine mutex.
  // `run` dostane tx-scoped handle → counter read+write atomicky na jednej tx.
  return db.transaction('rw', db.settings, run);
}

// Len prečíta aktuálny counter (bez incrementu) — pre náhľad/testy.
export async function peekInvoiceCounter() {
  const row = await db.settings.get(INVOICE_COUNTER_KEY);
  return Number(row?.value) || 0;
}
